import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { LoginButton } from '@packages/shared/src/components/Button/LoginButton/LoginButton';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import type { ReactNode, FC } from 'react';
import { Suspense, lazy } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/navigation';
import { buttonVariants } from '@packages/shared/src/ui/Button/Button.variants';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { Card, CardContent, CardHeader, CardTitle } from '@packages/shared/src/ui/Card/Card';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Link } from '../../../Link/Link';
import { AccountOverviewFallback } from '../../AccountOverview/AccountOverviewFallback';

type AccountSlotProps = {
  onLogin: () => void;
};

const messages = defineMessages({
  ADDITIONAL_HEADER: {
    id: 'slot.account.additionalfunctions.header',
    defaultMessage: 'Weitere Funktionen',
  },
  SERVICE_HELP: {
    id: 'slot.account.additionalfunctions.servicehelp',
    defaultMessage: 'Service & Hilfe',
  },
  WISHLIST: {
    id: 'slot.account.additionalfunctions.wishlist',
    defaultMessage: 'Merkzettel',
  },
  NEWSLETTER: {
    id: 'slot.account.additionalfunctions.newsletter',
    defaultMessage: 'Newsletter',
  },
  ADDRESSBOOK: {
    id: 'slot.account.additionalfunctions.addressbook',
    defaultMessage: 'Adressbuch',
  },
  LINK_NEWSLETTER: {
    id: 'slot.account.additionalfunctions.newsletter.link',
    defaultMessage: '/service-hilfe/newsletter/',
  },
  PAYBACK: {
    id: 'slot.account.additionalfunctions.payback',
    defaultMessage: 'Mein PAYBACK',
  },
});

const LazyOverview = lazy(
  () => import(/* webpackChunkName: 'AccountOverview' */ '../../AccountOverview/AccountOverview'),
);
const linkClass = cn(
  buttonVariants({ size: 'base', variant: 'link' }),
  'text-size-body2 bg-color-background-base flex h-auto items-center justify-start gap-0.5 border-none p-0 no-underline',
);
const CardAction: FC<{
  icon: ReactNode;
  text: string;
  href: string;
}> = ({ icon, text, href }) => (
  <Link href={href} className={linkClass}>
    {icon}
    <span className="flex-grow">{text}</span>
  </Link>
);

export const AdditionalFunctions: FC = () => {
  const { formatMessage } = useIntl();
  const { locale } = useI18n();
  const {
    headerLinks,
    order: {
      paths,
      payback: { enabled: paybackEnabled },
    },
  } = useConfig();

  return (
    <div className="bg-color-grey-light p-1">
      <Card>
        <CardHeader hasDivider>
          <CardTitle>{formatMessage(messages.ADDITIONAL_HEADER)}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2.5 px-2 pb-1 pt-1.5">
          <CardAction
            icon={<span className="icon-emp-service h-3 w-3" />}
            text={formatMessage(messages.SERVICE_HELP)}
            href={headerLinks[locale].serviceLink}
          />
          <CardAction
            icon={<span className="icon-emp-wishlist h-3 w-3" />}
            text={formatMessage(messages.WISHLIST)}
            href={getLocalizedPath(paths.wishlist, locale)}
          />
          <CardAction
            icon={<span className="icon-emp-newsletter h-3 w-3" />}
            text={formatMessage(messages.NEWSLETTER)}
            href={formatMessage(messages.LINK_NEWSLETTER)}
          />
          <CardAction
            icon={<span className="icon-emp-adressbook h-3 w-3" />}
            text={formatMessage(messages.ADDRESSBOOK)}
            href={getLocalizedPath(paths.myAccountAddresses, locale)}
          />
          {paybackEnabled && (
            <CardAction
              icon={<span className="icon-emp-payback-colored h-3 w-3" />}
              text={formatMessage(messages.PAYBACK)}
              href={getLocalizedPath(paths.myAccountPayback, locale)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const AccountSlot: FC<AccountSlotProps> = ({ onLogin }) => {
  const { loggedIn } = useLoginState();
  const { formatMessage } = useIntl();
  const { locale } = useI18n();

  const config = useConfig();
  const router = useRouter();

  return (
    <>
      {loggedIn ? (
        <div className="bg-color-grey-light px-1 py-2" data-testid="account-slot-loggedin">
          <ErrorBoundary
            fallback={(reset) => (
              <div>
                <span>
                  {formatMessage({
                    id: 'Navigation.Account.customerData.error',
                    defaultMessage: 'Nutzerdaten konnten nicht abgerufen werden',
                  })}
                </span>
                <Button color="error" variant="color-button" onClick={reset}>
                  Wiederholen
                </Button>
              </div>
            )}
          >
            <Suspense fallback={<AccountOverviewFallback />}>
              <LazyOverview />
            </Suspense>
          </ErrorBoundary>
        </div>
      ) : (
        <div className="px-3 py-1">
          <LoginButton
            fullWidth
            onClick={
              config.lifeSupportForBasicallyDeadTenant
                ? () => {
                    router.push(getLocalizedPath(config.order.paths.myAccountOverview, locale));
                  }
                : onLogin
            }
          />
        </div>
      )}
      <AdditionalFunctions />
    </>
  );
};
